import { useEffect, useState } from "react";
import { Loader2, Search, Send, UserRound } from "lucide-react";
import { toast } from "react-toastify";

import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { cn } from "../../lib/utils";
import api from "../../services/api";
import toastError from "../../errors/toastError";

// Enviar contato: o cartão que o cliente toca e já conversa.
//
// Indicar o telefone do técnico colando o número no meio do texto obriga o
// cliente a copiar, abrir a agenda e digitar. Aqui sai como cartão de verdade
// — o mesmo que a conversa já sabe desenhar quando o cliente manda um.
//
// Busca na agenda (o padrão da transferência de atendimento) e, para quem
// ainda não está cadastrado, nome e número na hora.
const EnviarContatoModal = ({ aberto, aoFechar, ticketId }) => {
	const [busca, setBusca] = useState("");
	const [opcoes, setOpcoes] = useState([]);
	const [procurando, setProcurando] = useState(false);
	const [escolhidos, setEscolhidos] = useState([]);
	const [nome, setNome] = useState("");
	const [numero, setNumero] = useState("");
	const [enviando, setEnviando] = useState(false);

	useEffect(() => {
		if (busca.trim().length < 3) {
			// Em timeout de 0: limpar a lista direto no corpo do efeito é
			// setState em cascata, e o lint recusa.
			const limpar = setTimeout(() => setOpcoes([]), 0);
			return () => clearTimeout(limpar);
		}

		const espera = setTimeout(() => {
			setProcurando(true);
			api
				.get("/contacts", { params: { searchParam: busca } })
				.then(({ data }) => setOpcoes(data?.contacts || []))
				.catch(() => undefined)
				.finally(() => setProcurando(false));
		}, 400);

		return () => clearTimeout(espera);
	}, [busca]);

	const alternar = (contato) => {
		setEscolhidos((anteriores) =>
			anteriores.some((c) => c.contactId === contato.id)
				? anteriores.filter((c) => c.contactId !== contato.id)
				: [
						...anteriores,
						{
							contactId: contato.id,
							nome: contato.name,
							numero: contato.number,
						},
					],
		);
	};

	const adicionarAvulso = () => {
		const digitos = numero.replace(/\D/g, "");

		if (digitos.length < 8) {
			toast.error("Informe um número válido, com DDD.");
			return;
		}

		setEscolhidos((anteriores) => [
			...anteriores,
			{ nome: nome.trim() || digitos, numero: digitos },
		]);
		setNome("");
		setNumero("");
	};

	const enviar = async () => {
		if (!escolhidos.length) return;

		setEnviando(true);

		try {
			await api.post(`/messages/${ticketId}/contact`, {
				contatos: escolhidos,
			});

			toast.success(
				escolhidos.length > 1
					? `${escolhidos.length} contatos enviados.`
					: "Contato enviado.",
			);
			setEscolhidos([]);
			setBusca("");
			aoFechar();
		} catch (err) {
			toastError(err);
		}

		setEnviando(false);
	};

	return (
		<Dialog open={aberto} onOpenChange={(estado) => !estado && aoFechar()}>
			<DialogContent className="max-h-[90vh] max-w-md overflow-y-auto">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<UserRound className="h-4 w-4 text-primary" />
						Enviar contato
					</DialogTitle>
					<DialogDescription>
						Escolha na agenda ou informe nome e número.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-1.5">
					<Label htmlFor="busca-contato">Buscar na agenda</Label>
					<div className="relative">
						<Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
						<Input
							id="busca-contato"
							value={busca}
							onChange={(e) => setBusca(e.target.value)}
							placeholder="Nome ou número"
							className="pl-9"
							disabled={enviando}
						/>
						{procurando && (
							<Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
						)}
					</div>
				</div>

				{opcoes.length > 0 && (
					<ul className="max-h-44 divide-y overflow-y-auto rounded-lg border">
						{opcoes.map((contato) => {
							const marcado = escolhidos.some(
								(c) => c.contactId === contato.id,
							);

							return (
								<li key={contato.id}>
									<button
										type="button"
										onClick={() => alternar(contato)}
										className={cn(
											"flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-muted/60",
											marcado && "bg-primary/10",
										)}
									>
										<span className="min-w-0">
											<span className="block truncate font-medium">
												{contato.name}
											</span>
											<span className="block truncate text-xs text-muted-foreground">
												{contato.number}
											</span>
										</span>
										{marcado && (
											<span className="text-xs font-medium text-primary">
												escolhido
											</span>
										)}
									</button>
								</li>
							);
						})}
					</ul>
				)}

				<div className="space-y-1.5 rounded-lg border border-dashed p-3">
					<Label className="text-xs">Ou informe na hora</Label>
					<div className="flex gap-2">
						<Input
							value={nome}
							onChange={(e) => setNome(e.target.value)}
							placeholder="Nome"
							disabled={enviando}
						/>
						<Input
							value={numero}
							onChange={(e) => setNumero(e.target.value)}
							placeholder="5511999999999"
							inputMode="numeric"
							disabled={enviando}
						/>
						<Button
							type="button"
							variant="secondary"
							onClick={adicionarAvulso}
							disabled={enviando}
						>
							Incluir
						</Button>
					</div>
				</div>

				{escolhidos.length > 0 && (
					<div className="space-y-1.5">
						<Label className="text-xs">
							Vai no envio ({escolhidos.length})
						</Label>
						<ul className="space-y-1">
							{escolhidos.map((contato, indice) => (
								<li
									key={`${contato.numero}-${indice}`}
									className="flex items-center justify-between gap-2 rounded-lg border bg-muted/40 px-3 py-1.5 text-sm"
								>
									<span className="min-w-0">
										<span className="block truncate font-medium">
											{contato.nome}
										</span>
										<span className="block truncate text-xs text-muted-foreground">
											{contato.numero}
										</span>
									</span>
									<Button
										variant="ghost"
										size="sm"
										className="text-xs text-muted-foreground"
										onClick={() =>
											setEscolhidos((anteriores) =>
												anteriores.filter((_, i) => i !== indice),
											)
										}
									>
										tirar
									</Button>
								</li>
							))}
						</ul>
					</div>
				)}

				<div className="flex justify-end gap-2">
					<Button variant="ghost" onClick={aoFechar} disabled={enviando}>
						Cancelar
					</Button>
					<Button
						className="gap-2"
						onClick={enviar}
						disabled={!escolhidos.length || enviando}
					>
						{enviando ? (
							<Loader2 className="h-4 w-4 animate-spin" />
						) : (
							<Send className="h-4 w-4" />
						)}
						Enviar
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default EnviarContatoModal;
